import { Button, Checkbox, FormControlLabel, IconButton, Paper, Snackbar, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import type { DesktopNoteHandoff } from '@/hooks/useDesktopNoteHandoff';

/**
 * The handoff offer, shown over the note.
 *
 * A `Snackbar` rather than a `Dialog` on purpose: the note underneath must stay
 * scrollable, clickable, and readable the whole time. Nothing here traps focus,
 * hides the page from assistive tech, or dismisses on click-away — the only ways
 * out are the buttons and the close icon, all always on screen.
 */
const DesktopHandoffInterstitial = ({
  state,
  noteUrl,
  remember,
  setRemember,
  confirmOpen,
  stayInBrowser,
  dismiss,
}: DesktopNoteHandoff) => {
  if (state === 'idle' || !noteUrl) return null;

  const isAsking = state === 'ask';

  return (
    <Snackbar open anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
      <Paper
        elevation={6}
        role="dialog"
        aria-modal="false"
        aria-labelledby="desktop-handoff-title"
        sx={{ p: 2, maxWidth: 420, width: '100%' }}
      >
        <Stack gap="1rem">
          <Stack flexDirection="row" alignItems="flex-start" justifyContent="space-between" gap="0.5rem">
            <Typography id="desktop-handoff-title" variant="body1" fontWeight="700">
              {isAsking ? 'Open this note in the NoteGo desktop app?' : 'Opening in NoteGo…'}
            </Typography>
            <IconButton size="small" onClick={dismiss} aria-label="Close">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>

          {isAsking && (
            <FormControlLabel
              control={
                <Checkbox size="small" checked={remember} onChange={(event) => setRemember(event.target.checked)} />
              }
              label={<Typography variant="body2">Remember my choice</Typography>}
            />
          )}

          <Stack flexDirection="row" flexWrap="wrap" gap="0.5rem">
            {/* A real anchor, so the click reaches the OS protocol handler as a
                user gesture. The custom protocol never unloads this page. */}
            <Button
              variant="contained"
              color="primary"
              href={noteUrl}
              onClick={isAsking ? confirmOpen : undefined}
              size="small"
            >
              {isAsking ? 'Open in desktop app' : 'Open again'}
            </Button>

            <Button variant="text" color="primary" onClick={stayInBrowser} size="small">
              Stay in the browser
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Snackbar>
  );
};

export default DesktopHandoffInterstitial;
